"use client";

import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { PlaceAutocomplete } from "@/components/others/place-autocomplete";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { Map } from "@vis.gl/react-google-maps";
import { ChevronDown, MapPin, MapPinOff, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { MapControlsFAB } from "../search/map/map-controls-fab";
import { MapMarkerClusterer } from "../search/map/map-marker-clusterer";
import { ButtonActions } from "./action-buttons";
import { AddressFormFields } from "./address-form-fields";
import { LocationDetails } from "./address-location-details";
import { useFormContext } from "./context/FormProvider";
import { useAddressMap, type AddressFormData } from "./hooks/useAddressMap";
interface StepAddressProps {
  onNext: (data: AddressFormData) => void;
  onBack?: () => void;
  isLoading?: boolean;
}
export function StepAddress(t0) {
  const $ = _c(97);
  const {
    onNext,
    onBack,
    isLoading
  } = t0;
  let t1;
  if ($[0] !== onNext) {
    t1 = {
      onNext,
      isDraggable: true
    };
    $[0] = onNext;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    control,
    handleSubmit,
    propertyType,
    isCommunity,
    onSubmit,
    mapCenter,
    setMapCenter,
    nearbyProjects,
    showForm,
    setShowForm,
    searchInputValue,
    setSearchInputValue,
    isLoadingLocation,
    centerActivated,
    addressData,
    hasUserSelectedLocation,
    isHovered,
    setIsHovered,
    handlePlaceSelect,
    handleMapClick,
    handleMapDragEnd,
    handleCurrentLocation,
    handleProjectClick,
    handleMapDrag,
    handleConfirmLocation,
    handleCloseForm,
    isDisabled,
    mapZoom,
    setMapZoom
  } = useAddressMap(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      control
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const watchedValues = useWatch(t2) as AddressFormData;
  const {
    state
  } = useFormContext();
  state.profile?.propertyType?.type;
  const isProjectSupported = state.profile?.propertyType?.type === "apartment";
  const hasProjectInfo = isProjectSupported ? Boolean(watchedValues.projectName) : true;
  const hasLocationInfo = Boolean(watchedValues.location.locality) && Boolean(watchedValues.location.pin);
  const canProceed = hasProjectInfo && hasLocationInfo;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      minHeight: "80vh"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== searchInputValue) {
    t5 = searchInputValue && {
      value: searchInputValue
    };
    $[6] = searchInputValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== setSearchInputValue) {
    t6 = () => setSearchInputValue(null);
    $[8] = setSearchInputValue;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      className: "w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 shadow-lg",
      style: t4,
      ...t5,
      onFocus: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handlePlaceSelect || $[14] !== t7) {
    t8 = <div className="flex-1 relative"><PlaceAutocomplete onPlaceSelect={handlePlaceSelect} placeholder="Search for project name or location..." inputProps={t7} /></div>;
    $[13] = handlePlaceSelect;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== isLoadingLocation) {
    t9 = isLoadingLocation ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="2" /></svg>;
    $[16] = isLoadingLocation;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== handleCurrentLocation || $[19] !== isLoadingLocation || $[20] !== t9) {
    t10 = <button type="button" onClick={handleCurrentLocation} disabled={isLoadingLocation} className="p-3 bg-white rounded-lg border border-gray-300 shadow-lg text-primary-600 hover:bg-gray-50 disabled:opacity-50 shrink-0 flex items-center justify-center h-[46px] w-[46px]" aria-label="Use Current Location">{t9}</button>;
    $[18] = handleCurrentLocation;
    $[19] = isLoadingLocation;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div className="absolute top-0 left-0 right-0 z-50 p-4"><div className="flex gap-2 items-center">{t8}{t10}</div></div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      width: "100%",
      height: "calc(100% - 100px)",
      position: "relative",
      zIndex: 10
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = centerActivated ? undefined : mapCenter;
  let t14;
  if ($[26] !== watchedValues.projectId) {
    t14 = !watchedValues.projectId && <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20"><div className="transform -translate-y-1/2 -mt-4 drop-shadow-xl"><MapPin className="w-10 h-10 text-primary-600 fill-current" /><div className="w-2 h-2 bg-black/20 rounded-full blur-[2px] absolute top-full left-1/2 -translate-x-1/2 -mt-1" /></div></div>;
    $[26] = watchedValues.projectId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== nearbyProjects) {
    t15 = nearbyProjects || [];
    $[28] = nearbyProjects;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== handleProjectClick || $[31] !== isHovered || $[32] !== mapZoom || $[33] !== setIsHovered || $[34] !== setMapCenter || $[35] !== setMapZoom || $[36] !== t15) {
    t16 = <MapMarkerClusterer projects={t15} isHovered={isHovered} setIsHovered={setIsHovered} mapZoom={mapZoom} handleProjectClick={handleProjectClick} setMapCenter={setMapCenter} setMapZoom={setMapZoom} isMobile={true} />;
    $[30] = handleProjectClick;
    $[31] = isHovered;
    $[32] = mapZoom;
    $[33] = setIsHovered;
    $[34] = setMapCenter;
    $[35] = setMapZoom;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <MapControlsFAB />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== handleMapClick || $[40] !== handleMapDrag || $[41] !== handleMapDragEnd || $[42] !== mapCenter || $[43] !== t13 || $[44] !== t14 || $[45] !== t16) {
    t18 = <div className="w-full h-full relative bg-gray-100"><ErrorBoundary fallback={_temp2}><Map mapId="address-picker-map" defaultCenter={mapCenter} defaultZoom={13} onClick={handleMapClick} gestureHandling="greedy" draggable={true} onDrag={handleMapDrag} onDragend={handleMapDragEnd} style={t12} mapTypeId="address-picker-map" disableDefaultUI={true} center={t13} reuseMaps={true}>{t14}{t16}{t17}</Map></ErrorBoundary></div>;
    $[39] = handleMapClick;
    $[40] = handleMapDrag;
    $[41] = handleMapDragEnd;
    $[42] = mapCenter;
    $[43] = t13;
    $[44] = t14;
    $[45] = t16;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== isLoadingLocation) {
    t19 = isLoadingLocation && <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-30"><div className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-md border border-white/20 shadow-lg rounded-full animate-in fade-in slide-in-from-top-4 duration-300"><svg className="animate-spin h-4 w-4 text-primary-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg><span className="text-sm font-medium text-gray-700">Fetching location...</span></div></div>;
    $[47] = isLoadingLocation;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== hasUserSelectedLocation || $[50] !== isLoadingLocation) {
    t20 = !hasUserSelectedLocation && !isLoadingLocation && <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-30 w-full flex justify-center px-4"><div className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white/95 backdrop-blur-md border border-gray-100 shadow-xl rounded-full animate-in fade-in slide-in-from-top-4 duration-300 min-w-[260px]"><p className="text-sm font-medium text-gray-700 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse ring-2 ring-primary-100" />Tap map to place pin or search above</p></div></div>;
    $[49] = hasUserSelectedLocation;
    $[50] = isLoadingLocation;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t18 || $[53] !== t19 || $[54] !== t20) {
    t21 = <div className="absolute inset-0 top-0 bottom-0 w-full h-full">{t18}{t19}{t20}</div>;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== addressData || $[57] !== handleConfirmLocation || $[58] !== mapCenter || $[59] !== showForm) {
    t22 = !showForm && <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-white shadow-2xl z-40"><div className="flex justify-center mb-2"><button aria-label="Drag Handle" type="button" onClick={handleConfirmLocation} className="flex flex-col items-center gap-1 p-2 cursor-pointer group"><div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-gray-400 transition-colors" /></button></div><div className="text-center mb-3"><div className="text-sm font-medium text-gray-900 mb-1 truncate">{addressData ? addressData.length > 60 ? `${addressData.substring(0, 60)}...` : addressData : `${mapCenter.lat.toFixed(6)}, ${mapCenter.lng.toFixed(6)}`}</div></div></div>;
    $[56] = addressData;
    $[57] = handleConfirmLocation;
    $[58] = mapCenter;
    $[59] = showForm;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== handleSubmit || $[62] !== onSubmit) {
    t23 = handleSubmit(onSubmit);
    $[61] = handleSubmit;
    $[62] = onSubmit;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  let t25;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-gray-400 transition-colors" />;
    t25 = <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    $[64] = t24;
    $[65] = t25;
  } else {
    t24 = $[64];
    t25 = $[65];
  }
  let t26;
  if ($[66] !== handleCloseForm) {
    t26 = <div className="flex justify-center mb-4"><button aria-label="Drag Handle" type="button" onClick={handleCloseForm} className="flex flex-col items-center gap-1 p-2 cursor-pointer group">{t24}{t25}</button></div>;
    $[66] = handleCloseForm;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== addressData || $[69] !== mapCenter) {
    t27 = <LocationDetails addressData={addressData} mapCenter={mapCenter} />;
    $[68] = addressData;
    $[69] = mapCenter;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <p className="text-xs text-neutral-500">Please fill in the address details below. If auto-fill didn't work, enter the information manually.</p>;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== control || $[73] !== isCommunity || $[74] !== isDisabled || $[75] !== isProjectSupported || $[76] !== propertyType) {
    t29 = <AddressFormFields control={control} propertyType={propertyType} isCommunity={isCommunity} isDisabled={isDisabled} isProjectSupported={isProjectSupported} />;
    $[72] = control;
    $[73] = isCommunity;
    $[74] = isDisabled;
    $[75] = isProjectSupported;
    $[76] = propertyType;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const t30 = !canProceed;
  let t31;
  if ($[78] !== isLoading || $[79] !== onBack || $[80] !== t30) {
    t31 = <ButtonActions next={true} onBack={onBack} disabled={t30} isLoading={isLoading} />;
    $[78] = isLoading;
    $[79] = onBack;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== t23 || $[83] !== t26 || $[84] !== t27 || $[85] !== t29 || $[86] !== t31) {
    t32 = <SheetOverlay><SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto rounded-t-2xl p-4" closeButton={false}><form onSubmit={t23} className="space-y-4">{t26}{t27}{t28}{t29}{t31}</form></SheetContent></SheetOverlay>;
    $[82] = t23;
    $[83] = t26;
    $[84] = t27;
    $[85] = t29;
    $[86] = t31;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  if ($[88] !== setShowForm || $[89] !== showForm || $[90] !== t32) {
    t33 = <Sheet isOpen={showForm} onOpenChange={setShowForm}>{t32}</Sheet>;
    $[88] = setShowForm;
    $[89] = showForm;
    $[90] = t32;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== t11 || $[93] !== t21 || $[94] !== t22 || $[95] !== t33) {
    t34 = <div className="relative h-full bg-white" style={t3}>{t11}{t21}{t22}{t33}</div>;
    $[92] = t11;
    $[93] = t21;
    $[94] = t22;
    $[95] = t33;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  return t34;
}
function _temp2() {
  return <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 backdrop-blur-sm z-50"><div className="text-center p-6 max-w-xs mx-auto"><div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><MapPinOff className="w-6 h-6 text-red-500" /></div><h3 className="text-gray-900 font-medium mb-1">Map Unavailable</h3><p className="text-sm text-gray-500 mb-4">We couldn't load the google map. Please check your connection.</p><button onClick={_temp} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-colors"><RefreshCw className="w-4 h-4" />Retry Loading</button></div></div>;
}
function _temp() {
  return window.location.reload();
}